import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { GptWrapper } from "../../core/GptWrapper";
import type { AdNodeManager } from "../../core/AdNodeManager";
import type { SlotName } from "../../config/platforms/types";
import { pushTrack } from "utils";
import { sendInfoTrack } from "./trackings/infoTracking";
import { sendLoadTimeTrack } from "./trackings/loadTimesTracking";
import { sendSizeInfoTrack } from "./trackings/sizeInfoTracking";
import { sizeToString } from "./helpers/getSlotSizes";
import { PrebidPlugin } from "../PrebidPlugin/PrebidPlugin";

import {
  type AdInfoClickPayload,
  InfoEvent,
  LoadTimeEvent,
  type LoadTimeTrackData,
  SizeInfoEvent,
  type TrackDataUnion,
} from "common-types";
import { ClicksDetector } from "./detectors/ClicksDetector";
import type { AdNode } from "../../core/AdNode";

import { AuctionTracker } from "./AuctionTracking";
import type { AdEngine } from "../../core/AdEngine";

interface TrackingPluginAPI {
  track: (
    event: TrackDataUnion["event"],
    payload: TrackDataUnion["payload"],
  ) => void;
}

/**
 * The TrackingPlugin is used to format data to be sent to metric tracking endpoints. 
 * 
 * The plugin puts event data into the `window.fandomTrack.q`. Meanwhile, the Tracking 
 * class in host.src.tracking.track reads data from that window object and sends it to
 * the appropriate tracking endpoint(s). 
 * 
 * Example Usage:
 * const trackingPlugin = this.usePlugin(TrackingPlugin);
 * trackingPlugin.track(
      Tracks.TYPE_OF_DATA_BEING_TRACKED,
      {
        ... // your data goes here
      }
   );
 * 
 */
export class TrackingPlugin extends AdEnginePlugin<TrackingPluginAPI> {
  private auctionTracker?: AuctionTracker;

  private readonly SLOTS_ELIGIBLE_TO_LOADING_TIME_TRACK: string[] = [
    "top_leaderboard",
    "top_boxad",
    "floor_adhesion",
    "interstitial",
  ];

  private readonly clickTrackingSlotMap = new Map<string, ClicksDetector>();

  private readonly slotsAlreadyTrackedForLoadTime: {
    requested: Map<string, boolean>;
    rendered: Map<string, boolean>;
  } = {
    requested: new Map<string, boolean>(),
    rendered: new Map<string, boolean>(),
  };

  private isSlotEligibleToLoadingTimeTrack(slotName: string): boolean {
    return this.SLOTS_ELIGIBLE_TO_LOADING_TIME_TRACK.includes(slotName);
  }

  private onGptSlotRequested(
    gptWrapper: GptWrapper,
    adNodeManager: AdNodeManager,
  ): void {
    gptWrapper.addEventListener(
      "slotRequested",
      (event: googletag.events.SlotResponseReceived) => {
        const slot = event.slot;
        const slotIdName = slot.getSlotElementId();

        sendInfoTrack(InfoEvent.SLOT_REQUESTED_INFO, slot, adNodeManager);

        if (!this.isSlotEligibleToLoadingTimeTrack(slotIdName)) {
          return;
        }

        if (!this.slotsAlreadyTrackedForLoadTime.requested.has(slotIdName)) {
          sendLoadTimeTrack(LoadTimeEvent.SLOT_REQUESTED, slotIdName);
          this.slotsAlreadyTrackedForLoadTime.requested.set(slotIdName, true);
        }
      },
    );
  }

  private onGptSlotRendered(
    gptWrapper: GptWrapper,
    adNodeManager: AdNodeManager,
  ): void {
    gptWrapper.addEventListener(
      "slotRenderEnded",
      (event: googletag.events.SlotRenderEndedEvent) => {
        if (event.isEmpty) {
          return;
        }

        const slot = event.slot;
        const slotIdName = slot.getSlotElementId() as SlotName;

        const node = adNodeManager.getAdNodeByName(slotIdName);
        if (!node) {
          return;
        }

        sendInfoTrack(
          InfoEvent.SLOT_RENDERED_INFO,
          slot,
          adNodeManager,
          sizeToString(event.size ?? ""),
        );
        sendSizeInfoTrack(
          SizeInfoEvent.SLOT_SIZE_ESTABLISHED,
          slot,
          adNodeManager,
          event.size,
        );

        this.attachClickTracking(slotIdName, node);

        if (!this.isSlotEligibleToLoadingTimeTrack(slotIdName)) {
          return;
        }

        if (!this.slotsAlreadyTrackedForLoadTime.rendered.has(slotIdName)) {
          sendLoadTimeTrack(LoadTimeEvent.SLOT_RENDERED, slotIdName);
          this.slotsAlreadyTrackedForLoadTime.rendered.set(slotIdName, true);
        }
      },
    );
  }

  private onGptResponseReceived(
    gptWrapper: GptWrapper,
    adNodeManager: AdNodeManager,
  ): void {
    gptWrapper.addEventListener(
      "slotResponseReceived",
      (event: googletag.events.SlotResponseReceived) => {
        const slot = event.slot;

        sendInfoTrack(InfoEvent.SLOT_CODE_SERVED_INFO, slot, adNodeManager);
      },
    );
  }

  /**
   * Listen for changes to the page visibility state and update the page
   * targeting `is_bg` value. This is so we can track how many GAM requests are
   * being made when the page is in the background.
   *
   * @param adEngine The AdEngine instance to get access to the gptWrapper to
   * set the targeting.
   */
  private onPageVisibilityChange(adEngine: AdEngine): void {
    adEngine.gptWrapper.setTargeting("is_bg", document.hidden ? "1" : "0");
  }

  private attachClickTracking(slotName: string, node: AdNode) {
    // Make click tracking idempotent
    if (this.clickTrackingSlotMap.has(slotName)) {
      return;
    }

    const sendClickTrackEvent = () => {
      const refreshValue = node.getRenderCount();
      const payload: AdInfoClickPayload = {
        slotName,
        rv: refreshValue,
      };
      pushTrack(InfoEvent.SLOT_CLICKED, payload);
    };

    const detector = new ClicksDetector(slotName, sendClickTrackEvent);
    detector.init();
    this.clickTrackingSlotMap.set(slotName, detector);
  }

  hooks: Hooks = {
    onInitPageTargeting: () => {
      pushTrack(LoadTimeEvent.AE_INIT, {
        time: performance.now(),
      } as LoadTimeTrackData["payload"]);
    },
    onAdEngineInit: (adEngine: AdEngine) => {
      // Listen to the Page Visibility API to track when the page goes into the background
      // or foreground and update the `is_bg` targeting accordingly.
      document.addEventListener(
        "visibilitychange",
        this.onPageVisibilityChange.bind(this, adEngine),
      );
      // Set the initial value for `is_bg` targeting
      this.onPageVisibilityChange(adEngine);
    },
    onBiddersInit: (adEngine: AdEngine) => {
      this.usePlugin(PrebidPlugin)?.addPrebidReadyCallback((pbjs: Prebid) => {
        // TODO: we can conditionally decide not to load this if this pageview does not
        // include bidder sampling. We would need to refactor the shouldSendToEndpoint logic
        // to record the decision in a place both ad-engine and host can access.
        this.auctionTracker = new AuctionTracker(pbjs, adEngine.adNodeManager);
      });
    },
    onBeforeFirstCallAdsRequest: (
      gptWrapper: GptWrapper,
      adNodeManager: AdNodeManager,
    ): GptWrapper => {
      this.onGptSlotRequested(gptWrapper, adNodeManager);
      this.onGptSlotRendered(gptWrapper, adNodeManager);
      this.onGptResponseReceived(gptWrapper, adNodeManager);

      return gptWrapper;
    },
  };

  api = {
    track: (
      event: TrackDataUnion["event"],
      payload: TrackDataUnion["payload"],
    ) => {
      pushTrack(event, payload);
    },
  };
}
